import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import AddTrainingMaterialDialog from '../../../components/ui/AddTrainingMaterialDialog';
import EditIcon from '../../../components/icons/EditIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import SearchIcon from '../../../components/icons/SearchIcon';
import CheckCircleIcon from '../../../components/icons/CheckCircleIcon';
import { AI_EDUTECH_TRAININGS, TrainingMaterialRecord } from '../../../data/aiEdutechTrainings';
import { TEACHER_TRAINING_MANAGE_BASE, teacherTrainingPath } from '../../../config/contentRoutes';

const AIEdutechTrainingManage: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'sharing' | 'scheduled'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [trainingMaterials, setTrainingMaterials] = useState<TrainingMaterialRecord[]>(AI_EDUTECH_TRAININGS);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const filteredMaterials = trainingMaterials.filter((material) => {
    const matchesFilter = filter === 'all' || material.status === filter;
    const matchesSearch = material.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatDateRange = (start: string, end: string) => `${start}~${end}`;

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        padding: '24px',
        boxSizing: 'border-box',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <button
              type="button"
              onClick={() => navigate(TEACHER_TRAINING_MANAGE_BASE)}
              style={{
                marginBottom: '12px',
                padding: '8px 14px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: 'var(--Color-fill-normal)',
                color: 'var(--Color-text-inverse)',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              ← 내 연수 관리
            </button>
            <h1 style={{ margin: '0 0 8px', fontSize: '28px', fontWeight: 700, color: 'var(--Color-text-strong)' }}>
              내 AI/에듀테크 연수
            </h1>
            <p style={{ margin: 0, fontSize: '15px', color: 'var(--Color-text-normal)' }}>
              공개 목록({teacherTrainingPath('AIEdutech')})에 노출할 내 연수 페이지를 관리합니다.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button variant="outline" size="medium" onClick={() => navigate(teacherTrainingPath('AIEdutech'))}>
              공개 목록 보기
            </Button>
            <Button variant="submit" onClick={() => setIsDialogOpen(true)} width="118px" height="40px">
              연수 등록
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {(['all', 'sharing', 'scheduled'] as const).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '13px',
                  color: filter === key ? 'var(--Color-text-strong)' : 'var(--Color-text-assistive)',
                }}
              >
                <CheckCircleIcon width={16} height={16} color={filter === key ? '#FF6F0F' : '#BABABA'} />
                {key === 'all' ? '전체' : key === 'sharing' ? '공유중' : '공유 예정'}
              </button>
            ))}
          </div>
          <div
            style={{
              flex: '1 1 240px',
              maxWidth: '360px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: 'var(--Color-background-normal)',
              borderRadius: '999px',
              border: '1px solid var(--Color-line-normal)',
            }}
          >
            <SearchIcon width={20} height={20} color="#222222" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="내 연수 검색"
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: '13px', background: 'transparent' }}
            />
          </div>
        </div>

        <Card padding="16px 20px" borderRadius="16px" background="var(--Color-background-normal)">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredMaterials.map((material, index) => (
              <div
                key={material.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '12px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderBottom: index < filteredMaterials.length - 1 ? '1px solid var(--Color-line-normal)' : 'none',
                }}
              >
                <div style={{ flex: 1, minWidth: '220px' }}>
                  <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>{material.name}</div>
                  <div style={{ fontSize: '13px', color: 'var(--Color-text-normal)', marginTop: '4px' }}>
                    {material.type} · {formatDateRange(material.sharePeriod.start, material.sharePeriod.end)} ·{' '}
                    {material.status === 'sharing' ? '공유중' : '공유 예정'}
                  </div>
                  {material.tsxPath && (
                    <div style={{ fontSize: '12px', color: 'var(--Color-text-assistive)', marginTop: '4px' }}>{material.tsxPath}</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  {material.tsxPath && (
                    <Button variant="outline" size="small" onClick={() => navigate(material.tsxPath!)}>
                      공개 미리보기
                    </Button>
                  )}
                  <button type="button" onClick={() => console.log('수정', material.id)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                    <EditIcon width={16} height={16} color="#989898" />
                  </button>
                  <button type="button" onClick={() => console.log('삭제', material.id)} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                    <TrashIcon width={16} height={16} color="#989898" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <AddTrainingMaterialDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={() => setTrainingMaterials(AI_EDUTECH_TRAININGS)}
      />
    </div>
  );
};

export default AIEdutechTrainingManage;
